/**
 * Dashboard page - Life Snapshot view.
 * Shows system health status and placeholder sections for future module summaries.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchHealth } from '../utils/api';

interface HealthStatus {
  status: string;
  environment: string;
  checks: Record<string, { status: string; message?: string }>;
  timestamp: string;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadHealth() {
      try {
        const data = await fetchHealth();
        setHealth(data);
      } catch (err) {
        console.error('Failed to fetch health:', err);
      } finally {
        setLoading(false);
      }
    }

    loadHealth();
  }, []);

  return (
    <div className="page-container">
      <header className="page-header">
        <h1 className="page-title">Welcome home, {user?.name.split(' ')[0]} 👋</h1>
        <p className="page-subtitle">Here's your life at a glance</p>
      </header>

      <div className="dashboard-grid">
        {/* System Status Card */}
        <section className="card">
          <h2 className="card-title">System Status</h2>
          {loading ? (
            <p className="card-text muted">Checking...</p>
          ) : health ? (
            <div className="status-info">
              <div className={`status-badge status-${health.status}`}>
                {health.status}
              </div>
              <p className="card-text">
                Environment: <strong>{health.environment}</strong>
              </p>
              <div className="checks-list">
                {Object.entries(health.checks).map(([key, check]) => (
                  <div key={key} className="check-item">
                    <span className={`check-status check-${check.status}`}>
                      {check.status === 'ok' ? '✓' : '✗'}
                    </span>
                    <span className="check-name">{key.toUpperCase()}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="card-text error">Failed to load system status</p>
          )}
        </section>

        {/* Placeholder module summary cards */}
        <section className="card card-placeholder">
          <h2 className="card-title">💰 Finance at a Glance</h2>
          <p className="card-text muted">Enable the Finance module to see your financial snapshot</p>
        </section>

        <section className="card card-placeholder">
          <h2 className="card-title">💪 Health This Week</h2>
          <p className="card-text muted">Enable the Health module to track your fitness progress</p>
        </section>

        <section className="card card-placeholder">
          <h2 className="card-title">🎯 Active Goals</h2>
          <p className="card-text muted">Enable the Goals module to track your habits and objectives</p>
        </section>

        <section className="card card-placeholder">
          <h2 className="card-title">📚 Learning Progress</h2>
          <p className="card-text muted">Enable the Education module to track your learning journey</p>
        </section>
      </div>
    </div>
  );
}
